import React, { useState, useEffect, useRef } from 'react'
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  TextField,
  Divider
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { ExpandableCardList } from '../../../components/_WMS/ExpandableCardList/ExpandableCardList'
import CleaningServicesIcon from '@mui/icons-material/CleaningServices'
import { StepProps } from '../../../components/_WMS/Stepper'
import { AsnLocateResult, AsnLocateState, FlowType, FLOW_TYPE_ID, UnknownOption, eStepAsnLocate } from '../types'
import { OptionToggle } from '../../../components/_WMS/OptionToggle'
import { useSuppliers } from '../../../hooks/useSuppliers'
import { useSuppliersOrigin } from '../../../hooks/useSuppliersOrigin'
import { useApi } from '../../../hooks/useApi'
import { useSnackbar } from '../../../hooks/useSnackbar'
import { SnackBar } from '../../../components/SnackBar/Snackbar'
import { ApiResponse } from '../../../typings/ApiResponse'
import {
  getAsnReceptionBySupplierId,
  getAsnReceptionBySupplierOriginId,
  getAsnReceptionByPriority,
  getAsnReceptionBySkuEan
} from '../api'
import { Status } from '../../../hooks/useFetchState'

interface UnknownIdStepProps extends StepProps<AsnLocateState> {}

export const UnknownIdStep: React.FC<UnknownIdStepProps> = ({
  goToStep,
  setSharedState,
  sharedState
}) => {
  const [searchBy, setSearchBy] = useState<UnknownOption>('Prioridad')
  const [flowType, setFlowType] = useState<FlowType>(sharedState.defaultFlowType ?? 'Recepción')
  const [priorityResults, setPriorityResults] = useState<AsnLocateResult[]>([])
const [skuEan, setSkuEan] = useState('')
  const prevFlowType = useRef(flowType)
  const [skuResults, setSkuResults] = useState<AsnLocateResult[]>([])

  const { suppliersState: { suppliers }, getSuppliers } = useSuppliers()
  const { suppliersOriginState: { suppliersOrigin }, getSuppliersOrigin } = useSuppliersOrigin()
  const { callApi, state } = useApi()
  const { isActive, isSuccess, message, openSnackBar } = useSnackbar()

  const filterOptions: UnknownOption[] = ['Prioridad', 'SKU/EAN', 'Proveedor', 'Origen']

  useEffect(() => {
    setSharedState((prev) => ({ ...prev, stepTitle: 'Busca la ASN por cualquiera de estos criterios' }))
    return () => {
      setSharedState((prev) => ({ ...prev, stepTitle: undefined }))
    }
  }, [])

  useEffect(() => {
    if (searchBy === 'Proveedor' && !suppliers?.length) getSuppliers()
    if (searchBy === 'Origen' && !suppliersOrigin?.length) getSuppliersOrigin()
    if (searchBy === 'Prioridad') {
      setPriorityResults([])
      callApi(getAsnReceptionByPriority(FLOW_TYPE_ID[flowType]))
        .then((response: ApiResponse<AsnLocateResult>) => {
          if (response.totalItems === 0) {
            openSnackBar('No se encontraron ASNs', false)
          } else {
            setPriorityResults(response.items)
          }
        })
        .catch(() => openSnackBar('Error al buscar el registro', false))
    }
    if (searchBy === 'SKU/EAN') setSkuEan('')
  }, [searchBy])

  useEffect(() => {
    if (flowType === prevFlowType.current) return
    prevFlowType.current = flowType

    if (searchBy === 'Prioridad') {
      setPriorityResults([])
      callApi(getAsnReceptionByPriority(FLOW_TYPE_ID[flowType]))
        .then((response: ApiResponse<AsnLocateResult>) => {
          if (response.totalItems === 0) {
            openSnackBar('No se encontraron ASNs', false)
          } else {
            setPriorityResults(response.items)
          }
        })
        .catch(() => openSnackBar('Error al buscar el registro', false))
    }
    if (searchBy === 'SKU/EAN' && skuEan.trim()) {
      handleSkuEanSearch()
    }
  }, [flowType])

  const handleApiResponse = (response: ApiResponse<AsnLocateResult>) => {
    if (response.totalItems === 0) {
      openSnackBar('No existen registro', false)
      return
    }
    const single = response.totalItems === 1 ? response.items[0] : null
    setSharedState((prev) => ({ ...prev, results: response.items, selectedResult: single }))
    goToStep(eStepAsnLocate.ResultsStep)
  }

  const handlePrioritySelect = (item: AsnLocateResult) => {
    setSharedState((prev) => ({ ...prev, selectedResult: item }))
    goToStep(item.withLpn ? eStepAsnLocate.LpnStep : eStepAsnLocate.LocateStepV2)
  }

  const handleSkuEanSearch = () => {
    if (!skuEan.trim()) return
    setSkuResults([])
    callApi(getAsnReceptionBySkuEan(skuEan.trim(), FLOW_TYPE_ID[flowType]))
      .then((response: ApiResponse<AsnLocateResult>) => {
        if (response.totalItems === 0) {
          openSnackBar('No se encuentra ASN para el SKU indicado', false)
        } else {
          setSkuResults(response.items)
        }
      })
      .catch((error) => openSnackBar(error, false))
  }

  const callSupplier = (supplierId: number) => {
    callApi(getAsnReceptionBySupplierId(supplierId, FLOW_TYPE_ID[flowType]))
      .then(handleApiResponse)
      .catch(() => openSnackBar('Error al buscar el registro', false))
  }

  const callOrigin = (originId: number) => {
    callApi(getAsnReceptionBySupplierOriginId(originId, FLOW_TYPE_ID[flowType]))
      .then(handleApiResponse)
      .catch(() => openSnackBar('Error al buscar el registro', false))
  }

  const isLoading = state.status === Status.Loading
  const isLoadingSuppliers = searchBy === 'Proveedor' && !suppliers?.length
  const isLoadingOrigins = searchBy === 'Origen' && !suppliersOrigin?.length
  const isLoadingPriority = searchBy === 'Prioridad' && isLoading

  const itemSx = {
    display: 'flex',
    alignItems: 'center',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    overflow: 'hidden',
    cursor: 'pointer',
    '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.10)' }
  }

  const chevronSx = {
    width: 44,
    backgroundColor: 'primary.main',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'stretch'
  }

  const asnFields = [
    { label: 'Proveedor', getValue: (item: AsnLocateResult) => item.supplierName },
    { label: 'Origen', getValue: (item: AsnLocateResult) => item.supplierOriginName },
    { label: 'Unidades Totales', getValue: (item: AsnLocateResult) => item.unitsQty },
    { label: 'Marca', getValue: (item: AsnLocateResult) => item.brandName },
  ]

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', padding: 1.5, gap: 1, height: '100%', overflow: 'hidden' }}>
      <OptionToggle
        options={['Recepción', 'Devoluciones']}
        defaultValue={sharedState.defaultFlowType ?? 'Recepción'}
        onChange={(val) => setFlowType(val as FlowType)}
      />
      <Divider/>
      <Box>
        <Typography variant='overline' sx={{ fontWeight: 'bold', color: '#555', letterSpacing: 2 }}>
          Buscar por
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0 }}>
          {filterOptions.map((option) => (
            <Box key={option} sx={{ width: 'calc(50% - 4px)' }}>
              <Button
                fullWidth
                onClick={() => setSearchBy(option)}
                variant={searchBy === option ? 'contained' : 'outlined'}
                sx={{ borderRadius: '20px', textTransform: 'none', fontWeight: 600, py: 0.6, fontSize: '0.85rem' }}
              >
                {option}
              </Button>
            </Box>
          ))}
        </Box>
      </Box>

      {/* SKU/EAN */}
      {searchBy === 'SKU/EAN' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1, overflow: 'hidden' }}>
          <Box sx={{ display: 'flex', gap: 1, pt: 1 }}>
            <TextField
              fullWidth
              variant='outlined'
              size='small'
              label='Ingresar SKU o EAN'
              value={skuEan}
              onChange={(e) => setSkuEan(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSkuEanSearch()}
              autoComplete='off'
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
            />
            <Button
              variant='contained'
              onClick={handleSkuEanSearch}
              disabled={!skuEan.trim() || isLoading}
              sx={{ borderRadius: '10px', minWidth: 48, px: 1.5 }}
            >
              {isLoading
                ? <CircularProgress size={18} color='inherit' />
                : <SearchIcon />}
            </Button>
            <Button
              variant='outlined'
              onClick={() => { setSkuEan(''); setSkuResults([]) }}
              disabled={!skuEan && skuResults.length === 0}
              sx={{ borderRadius: '10px', minWidth: 48, px: 1.5 }}
            >
              <CleaningServicesIcon />
            </Button>
          </Box>

          {skuResults.length > 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography variant='overline' sx={{ fontWeight: 'bold', color: '#555', letterSpacing: 2 }}>
                Resultados
              </Typography>
              <Box sx={{ maxHeight: 250, overflowY: 'auto' }}>
                <ExpandableCardList
                  items={skuResults}
                  getKey={(item) => item.asnId}
                  renderHeader={(item) => (
                    <Typography variant='body2' fontWeight='bold'>{item.asnNumber}</Typography>
                  )}
                  fields={asnFields}
                  onSelect={handlePrioritySelect}
                />
              </Box>
            </Box>
          )}
        </Box>
      )}

      {/* Prioridad */}
      {searchBy === 'Prioridad' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant='overline' sx={{ fontWeight: 'bold', color: '#555', letterSpacing: 2 }}>
            Resultados
          </Typography>
          {isLoadingPriority && (
            <Box sx={{ display: 'flex', justifyContent: 'center', pt: 2 }}>
              <CircularProgress size={28} />
            </Box>
          )}
          <Box sx={{ maxHeight: 250, overflowY: 'auto' }}>
            <ExpandableCardList
              items={priorityResults}
              getKey={(item) => item.asnId}
              renderHeader={(item) => (
                <>
                  <Typography variant='body2' color='text.secondary'>{item.priority}</Typography>
                  <Typography variant='body2' fontWeight='bold'>{item.asnNumber}</Typography>
                </>
              )}
              fields={asnFields}
              onSelect={handlePrioritySelect}
            />
          </Box>
        </Box>
      )}

      {/* Proveedor / Origen */}
      {(searchBy === 'Proveedor' || searchBy === 'Origen') && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant='overline' sx={{ fontWeight: 'bold', color: '#555', letterSpacing: 2 }}>
            Resultados
          </Typography>
          <Box sx={{ maxHeight: 250, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1 }}>
          {(isLoadingSuppliers || isLoadingOrigins) && (
            <Box sx={{ display: 'flex', justifyContent: 'center', pt: 2 }}>
              <CircularProgress size={28} />
            </Box>
          )}
          {searchBy === 'Proveedor' && suppliers?.map((s) => (
            <Box key={s.supplierId} onClick={() => callSupplier(s.supplierId)} sx={itemSx}>
              <Typography variant='body2' sx={{ flex: 1, px: 2, py: 1.2, fontWeight: 500 }}>
                {s.supplierName}
              </Typography>
              <Box sx={chevronSx}><ChevronRightIcon sx={{ color: '#fff', fontSize: 22 }} /></Box>
            </Box>
          ))}
          {searchBy === 'Origen' && suppliersOrigin?.map((o) => (
            <Box key={o.id} onClick={() => callOrigin(o.id)} sx={itemSx}>
              <Typography variant='body2' sx={{ flex: 1, px: 2, py: 1.2, fontWeight: 500 }}>
                {o.supplierOriginName}
              </Typography>
              <Box sx={chevronSx}><ChevronRightIcon sx={{ color: '#fff', fontSize: 22 }} /></Box>
            </Box>
          ))}
          </Box>
        </Box>
      )}

      <SnackBar message={message} open={isActive} status={isSuccess ? 'success' : 'error'} />
    </Box>
  )
}
